import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { EnvelopeSimple, PaperPlaneTilt, Spinner, CaretRight, ClockCounterClockwise, ArrowClockwise, CheckCircle, XCircle } from '@phosphor-icons/react';

const DELIVERY_STYLES = {
    delivered: 'bg-[#dcfce7] text-[#15803d]',
    sent: 'bg-[#fef9c3] text-[#a16207]',
    delivery_delayed: 'bg-[#ffedd5] text-[#c2410c]',
    bounced: 'bg-[#fee2e2] text-[#b91c1c]',
    complained: 'bg-[#fee2e2] text-[#b91c1c]',
    failed: 'bg-[#fee2e2] text-[#b91c1c]',
    canceled: 'bg-[#e2e8f0] text-[#475569]',
};

function deliveryBadge(status) {
    if (!status) return <span className="text-xs text-[#94a3b8]" data-testid="delivery-status">— not checked</span>;
    const cls = DELIVERY_STYLES[status] || 'bg-[#e2e8f0] text-[#475569]';
    const label = status.replace(/_/g, ' ');
    return <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${cls}`} data-testid="delivery-status">{label}</span>;
}

export default function EmailPreviewTool() {
    const { api, user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [selected, setSelected] = useState(null);
    const [preview, setPreview] = useState(null); // { subject, html }
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [sending, setSending] = useState(false);
    const [recipient, setRecipient] = useState('');
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);

    const loadLogs = useCallback(() => {
        setLogsLoading(true);
        api().get('/admin/email-logs')
            .then(res => setLogs(res.data.logs || []))
            .catch(() => {})
            .finally(() => setLogsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkDeliveryStatus = async () => {
        setCheckingStatus(true);
        try {
            const res = await api().post('/admin/email-logs/refresh-status');
            setLogs(res.data.logs || []);
            if (res.data.error) {
                toast.error(res.data.error);
            } else {
                toast.success(`Delivery status updated (${res.data.checked} checked)`);
            }
        } catch (err) {
            toast.error('Failed to check delivery status');
        } finally {
            setCheckingStatus(false);
        }
    };

    useEffect(() => {
        api().get('/admin/email-templates')
            .then(res => {
                const list = res.data.templates || [];
                setTemplates(list);
                if (list.length) setSelected(list[0].key);
            })
            .catch(() => toast.error('Failed to load email templates'));
        setRecipient(user?.email || '');
        loadLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selected) return;
        setLoadingPreview(true);
        setPreview(null);
        api().get(`/admin/email-preview/${selected}`)
            .then(res => setPreview(res.data))
            .catch(() => toast.error('Failed to load preview'))
            .finally(() => setLoadingPreview(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const handleSend = async () => {
        setSending(true);
        try {
            const res = await api().post(`/admin/email-preview/${selected}`, { to: recipient || undefined });
            toast.success(`Sample sent to ${res.data.sent_to} (id: ${res.data.email_id || 'n/a'})`);
            loadLogs();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'Failed to send sample email');
        } finally {
            setSending(false);
        }
    };

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="email-preview-tool">
            <div className="flex items-center gap-2 mb-2">
                <EnvelopeSimple weight="duotone" className="text-[#22c55e]" size={32} />
                <h1 className="font-fredoka text-2xl font-bold text-[#1e293b]">Email Previews</h1>
            </div>
            <p className="text-sm text-[#475569] mb-6 max-w-2xl">
                Preview each transactional email with sample data, and send a sample to your inbox to confirm it renders correctly.
            </p>

            <div className="grid lg:grid-cols-[280px_1fr] gap-6">
                {/* Template list */}
                <div className="space-y-2" data-testid="email-template-list">
                    {templates.map(t => (
                        <button
                            key={t.key}
                            onClick={() => setSelected(t.key)}
                            className={`w-full text-left p-3 rounded-xl border-3 border-[#0f172a] transition-all flex items-center justify-between gap-2 ${selected === t.key ? 'bg-[#22c55e] text-white shadow-[3px_3px_0px_#0f172a]' : 'bg-white text-[#0f172a] hover:-translate-y-0.5'}`}
                            data-testid={`email-tpl-${t.key}`}
                        >
                            <span>
                                <span className="block font-fredoka font-bold text-sm">{t.label}</span>
                                <span className={`block text-xs ${selected === t.key ? 'text-white/80' : 'text-[#64748b]'}`}>{t.description}</span>
                            </span>
                            <CaretRight weight="bold" size={16} className="shrink-0" />
                        </button>
                    ))}
                </div>

                {/* Preview + send */}
                <div className="space-y-4">
                    <div className="bg-white rounded-2xl border-4 border-[#0f172a] p-4 shadow-[4px_4px_0px_#0f172a]">
                        <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
                            <div className="flex-1">
                                <label className="block text-xs font-bold text-[#0f172a] mb-1 uppercase tracking-wide">Send sample to</label>
                                <input
                                    value={recipient}
                                    onChange={e => setRecipient(e.target.value)}
                                    placeholder="your@email.com"
                                    className="w-full px-4 py-2.5 rounded-xl border-3 border-[#0f172a] font-semibold text-sm focus:outline-none"
                                    data-testid="email-recipient-input"
                                />
                            </div>
                            <button
                                onClick={handleSend}
                                disabled={sending || !selected}
                                className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border-4 border-[#0f172a] bg-[#22c55e] text-white font-fredoka font-bold shadow-[3px_3px_0px_#0f172a] disabled:opacity-50 hover:-translate-y-0.5 transition-transform"
                                data-testid="email-send-btn"
                            >
                                {sending ? <Spinner className="animate-spin" size={20} /> : <PaperPlaneTilt weight="bold" size={20} />}
                                Send Sample
                            </button>
                        </div>
                        {preview && (
                            <p className="text-sm text-[#475569] mb-2" data-testid="email-subject">
                                <span className="font-bold text-[#0f172a]">Subject:</span> {preview.subject}
                            </p>
                        )}
                    </div>

                    <div className="bg-[#f1f5f9] rounded-2xl border-4 border-[#0f172a] overflow-hidden shadow-[4px_4px_0px_#0f172a]" style={{ height: 520 }}>
                        {loadingPreview ? (
                            <div className="h-full flex items-center justify-center text-[#64748b]">
                                <Spinner className="animate-spin" size={28} />
                            </div>
                        ) : preview ? (
                            <iframe
                                title="email-preview"
                                srcDoc={preview.html}
                                className="w-full h-full bg-white"
                                sandbox=""
                                data-testid="email-preview-iframe"
                            />
                        ) : (
                            <div className="h-full flex items-center justify-center text-[#64748b] text-sm">Select a template to preview</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Delivery log */}
            <div className="mt-8" data-testid="email-log-section">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <ClockCounterClockwise weight="duotone" className="text-[#22c55e]" size={26} />
                        <h2 className="font-fredoka text-xl font-bold text-[#1e293b]">Delivery Log</h2>
                    </div>
                    <div className="flex items-center gap-2">
                    <button
                        onClick={checkDeliveryStatus}
                        disabled={checkingStatus || logs.length === 0}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl border-3 border-[#0f172a] bg-[#38bdf8] text-white font-fredoka font-bold text-sm shadow-[2px_2px_0px_#0f172a] disabled:opacity-50 hover:-translate-y-0.5 transition-transform"
                        data-testid="email-log-check-status"
                    >
                        {checkingStatus ? <Spinner className="animate-spin" size={16} /> : <ClockCounterClockwise weight="bold" size={16} />} Check Delivery
                    </button>
                    <button
                        onClick={loadLogs}
                        disabled={logsLoading}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl border-3 border-[#0f172a] bg-white font-fredoka font-bold text-sm shadow-[2px_2px_0px_#0f172a] disabled:opacity-50 hover:-translate-y-0.5 transition-transform"
                        data-testid="email-log-refresh"
                    >
                        {logsLoading ? <Spinner className="animate-spin" size={16} /> : <ArrowClockwise weight="bold" size={16} />} Refresh
                    </button>
                    </div>
                </div>
                <div className="bg-white rounded-2xl border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] overflow-hidden">
                    {logs.length === 0 ? (
                        <div className="p-8 text-center text-[#64748b] text-sm">No emails sent yet.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="email-log-table">
                                <thead>
                                    <tr className="bg-[#f1f5f9] border-b-3 border-[#0f172a] text-left">
                                        <th className="px-4 py-3 font-fredoka font-bold text-[#0f172a]">Status</th>
                                        <th className="px-4 py-3 font-fredoka font-bold text-[#0f172a]">Delivery</th>
                                        <th className="px-4 py-3 font-fredoka font-bold text-[#0f172a]">To</th>
                                        <th className="px-4 py-3 font-fredoka font-bold text-[#0f172a] hidden sm:table-cell">Subject</th>
                                        <th className="px-4 py-3 font-fredoka font-bold text-[#0f172a] hidden md:table-cell">Type</th>
                                        <th className="px-4 py-3 font-fredoka font-bold text-[#0f172a]">When</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((l) => (
                                        <tr key={l.id} className="border-b border-[#e2e8f0] last:border-0" data-testid="email-log-row">
                                            <td className="px-4 py-3">
                                                {l.status === 'sent' ? (
                                                    <span className="inline-flex items-center gap-1 text-xs font-bold text-[#16a34a]"><CheckCircle weight="fill" size={16} /> Sent</span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-xs font-bold text-[#ef4444]" title={l.error || ''}><XCircle weight="fill" size={16} /> Failed</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">{deliveryBadge(l.delivery_status)}</td>
                                            <td className="px-4 py-3 text-[#0f172a] font-semibold break-all">{l.to}</td>
                                            <td className="px-4 py-3 text-[#475569] hidden sm:table-cell">{l.subject}</td>
                                            <td className="px-4 py-3 hidden md:table-cell">
                                                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#e0f2fe] text-[#0369a1]">{l.category || 'general'}</span>
                                            </td>
                                            <td className="px-4 py-3 text-[#64748b] whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString() : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
